import { Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyLoadQuery, useMutation, graphql } from 'react-relay';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { formatDisplayDate } from '@/utils/date';
import { Clock, Briefcase, DollarSign, FileText, Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DaysheetlyButton } from '@/components/ui/DaysheetlyButton';
import { FadeIn } from '@/components/animations/FadeIn';
import { DeleteConfirmation } from '@/components/ui/DeleteConfirmation';
import type { DashboardEntry } from '@/features/dashboard/components/DashboardTable';
import { EmptyState } from '@/components/emptyState/EmptyState';
import { CardSkeleton, TableSkeleton } from '@/components/loading/LoadingState';
import { DashboardTable } from '@/features/dashboard/components/DashboardTable';
import { TaskDialog, type TaskFormData } from '@/features/daySheetly/components/TaskDialog';
import { formatCurrency, formatDurationDecimal, formatDuration } from '@/utils/formatters';
import { toast } from 'sonner';
import type { DashboardPageQuery as DashboardPageQueryType } from '@/__generated__/DashboardPageQuery.graphql.ts';
import type { DashboardPageUpdateMutation as DashboardPageUpdateMutationType } from '@/__generated__/DashboardPageUpdateMutation.graphql.ts';
import type { DashboardPageDeleteMutation as DashboardPageDeleteMutationType } from '@/__generated__/DashboardPageDeleteMutation.graphql.ts';
import type { DashboardPageDuplicateMutation as DashboardPageDuplicateMutationType } from '@/__generated__/DashboardPageDuplicateMutation.graphql.ts';

const dashboardPageQuery = graphql`
  query DashboardPageQuery(
    $projectId: ID
    $startDate: String
    $endDate: String
  ) {
    projects {
      id
      name
      status
      budget
      hourlyRate
      currency
    }
    tasks(
      projectId: $projectId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      title
      date
      durationMinutes
      hourlyRate
      currency
      totalAmount
      billingStatus
      members {
        id
        name
      }
      project {
        id
        name
        color
      }
    }
  }
`;

const updateTaskMutation = graphql`
  mutation DashboardPageUpdateMutation(
    $id: ID!
    $title: String
    $description: String
    $date: String
    $endDate: String
    $duration: String
    $projectId: ID
    $hourlyRate: Float
    $currency: Currency
    $billingStatus: BillingStatus
    $memberIds: [ID!]
  ) {
    updateTask(
      id: $id
      title: $title
      description: $description
      date: $date
      endDate: $endDate
      duration: $duration
      projectId: $projectId
      hourlyRate: $hourlyRate
      currency: $currency
      billingStatus: $billingStatus
      memberIds: $memberIds
    ) {
      id
      title
      date
      durationMinutes
      totalAmount
      billingStatus
    }
  }
`;

const deleteTaskMutation = graphql`
  mutation DashboardPageDeleteMutation($id: ID!) {
    deleteTask(id: $id)
  }
`;

const duplicateTaskMutation = graphql`
  mutation DashboardPageDuplicateMutation($id: ID!) {
    duplicateTask(id: $id) {
      id
      title
      date
      durationMinutes
      totalAmount
      billingStatus
    }
  }
`;

export function DashboardPage() {
  const [viewMode, setViewMode] = useState<'monthly' | 'weekly'>('monthly');
  const navigate = useNavigate();

  const openAddTask = () => {
    navigate('/daysheetly?addTask=true');
  };

  const dateRange = useMemo(() => {
    const now = new Date();
    if (viewMode === 'weekly') {
      return {
        startDate: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        endDate: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    }
    return {
      startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  }, [viewMode]);

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex-1 space-y-6 p-4 sm:p-6">
        <FadeIn>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Track your time and review recent activity.
              </p>
            </div>
            <DaysheetlyButton
              onClick={openAddTask}
              data-testid="dashboard-add-task-button"
              startIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            >
              Add Task
            </DaysheetlyButton>
          </div>
        </FadeIn>

        <FadeIn delay={0.05}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('weekly')}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'weekly'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'monthly'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              Monthly
            </button>
            <span className="ml-2 text-sm text-muted-foreground">
              {formatDisplayDate(dateRange.startDate, 'MMM dd')} - {formatDisplayDate(dateRange.endDate, 'MMM dd, yyyy')}
            </span>
          </div>
        </FadeIn>

        <Suspense
          fallback={
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </div>
              <Card>
                <CardContent className="p-6">
                  <TableSkeleton rows={6} columns={6} />
                </CardContent>
              </Card>
            </div>
          }
        >
          <DashboardPageContent
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        </Suspense>
      </div>
    </div>
  );
}

function DashboardPageContent({ startDate, endDate }: { startDate: string; endDate: string }) {
  const data = useLazyLoadQuery<DashboardPageQueryType>(dashboardPageQuery, {
    projectId: null,
    startDate,
    endDate,
  });

  const [commitUpdate] = useMutation<DashboardPageUpdateMutationType>(updateTaskMutation);
  const [commitDelete] = useMutation<DashboardPageDeleteMutationType>(deleteTaskMutation);
  const [commitDuplicate] = useMutation<DashboardPageDuplicateMutationType>(duplicateTaskMutation);

  const [editingTask, setEditingTask] = useState<TaskFormData | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [pendingDeleteEntry, setPendingDeleteEntry] = useState<DashboardEntry | null>(null);
  const [pendingDeleteTask, setPendingDeleteTask] = useState<TaskFormData | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const projectOptions = useMemo(
    () => data.projects.map((p) => ({
      id: p.id,
      name: p.name,
      hourlyRate: p.hourlyRate,
      currency: p.currency as 'BRL' | 'USD',
    })),
    [data.projects],
  );

  const entryToTaskFormData = useCallback(
    (entry: DashboardEntry): TaskFormData => ({
      id: entry.id,
      title: entry.title,
      description: '',
      duration: formatDuration(entry.durationMinutes),
      projectId: entry.project?.id ?? '',
      date: entry.date,
      endDate: undefined,
      hourlyRate: entry.hourlyRate,
      currency: entry.currency as 'BRL' | 'USD',
      billingStatus: entry.billingStatus as 'unbilled' | 'billed',
      memberIds: entry.members.map((m) => m.id),
    }),
    [],
  );

  const handleEdit = useCallback((entry: DashboardEntry) => {
    setEditingTask(entryToTaskFormData(entry));
    setIsEditDialogOpen(true);
  }, [entryToTaskFormData]);

  const handleDuplicate = useCallback(
    (entry: DashboardEntry) => {
      commitDuplicate({
        variables: { id: entry.id },
        onCompleted: () => {
          toast.success('Task duplicated');
        },
        onError: () => {
          toast.error('Failed to duplicate task');
        },
      });
    },
    [commitDuplicate],
  );

  const handleDeleteRequest = useCallback(
    (entry: DashboardEntry) => {
      const taskData = entryToTaskFormData(entry);
      setPendingDeleteEntry(entry);
      setPendingDeleteTask(taskData);
      setIsDeleteDialogOpen(true);
    },
    [entryToTaskFormData],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!pendingDeleteEntry) return;
    commitDelete({
      variables: { id: pendingDeleteEntry.id },
      onCompleted: () => {
        toast.success('Task deleted');
        setIsDeleteDialogOpen(false);
        setPendingDeleteEntry(null);
        setPendingDeleteTask(null);
      },
      onError: () => {
          toast.error('Failed to delete task');
        },
    });
  }, [commitDelete, pendingDeleteEntry]);

  const handleSaveEdit = useCallback(
    (formData: TaskFormData) => {
      if (!formData.id) return;
      commitUpdate({
        variables: {
          id: formData.id,
          title: formData.title,
          description: formData.description || null,
          date: formData.date,
          endDate: formData.endDate || null,
          duration: formData.duration,
          projectId: formData.projectId,
          hourlyRate: formData.hourlyRate,
          currency: formData.currency,
          billingStatus: formData.billingStatus,
          memberIds: formData.memberIds,
        },
        onCompleted: () => {
          toast.success('Task updated');
          setIsEditDialogOpen(false);
          setEditingTask(null);
        },
        onError: () => {
          toast.error('Failed to update task');
        },
      });
    },
    [commitUpdate],
  );

  const dashboardEntries: DashboardEntry[] = useMemo(
    () =>
      data.tasks.map((entry) => ({
        id: entry.id,
        title: entry.title,
        date: entry.date as string,
        durationMinutes: entry.durationMinutes,
        hourlyRate: entry.hourlyRate,
        currency: entry.currency,
        totalAmount: entry.totalAmount,
        billingStatus: entry.billingStatus,
        members: entry.members.map((m) => ({ id: m.id, name: m.name })),
        project: entry.project
          ? { id: entry.project.id, name: entry.project.name, color: entry.project.color ?? null }
          : null,
      })),
    [data.tasks],
  );

  const defaultCurrency = data.projects[0]?.currency ?? 'BRL';

  // Compute summary from the entries already in scope (filtered by date
  // range), so Weekly ↔ Monthly recomputes the cards consistently.
  const summary = useMemo(() => {
    let totalMinutes = 0;
    let billableMinutes = 0;
    let unbilledMinutes = 0;
    let unbilledAmount = 0;
    for (const entry of data.tasks) {
      totalMinutes += entry.durationMinutes;
      if (entry.billingStatus === 'billed') {
        billableMinutes += entry.durationMinutes;
      } else if (entry.billingStatus === 'unbilled') {
        unbilledMinutes += entry.durationMinutes;
        unbilledAmount += entry.totalAmount;
      }
    }
    return {
      totalHours: Number((totalMinutes / 60).toFixed(2)),
      billableHours: Number((billableMinutes / 60).toFixed(2)),
      unbilledHours: Number((unbilledMinutes / 60).toFixed(2)),
      unbilledAmount: Number(unbilledAmount.toFixed(2)),
    };
  }, [data.tasks]);

  return (
    <>
      <FadeIn delay={0.1}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            icon={Clock}
            label="Total Hours"
            value={`${formatDurationDecimal(summary.totalHours * 60)} hours`}
            className="bg-card"
            iconClassName="text-primary"
            data-testid="metric-total-hours"
          />
          <MetricCard
            icon={Briefcase}
            label="Billable Hours"
            value={`${formatDurationDecimal(summary.billableHours * 60)} Hours`}
            className="bg-card"
            iconClassName="text-primary"
            data-testid="metric-billable-hours"
          />
          <MetricCard
            icon={FileText}
            label="Unbilled Hours"
            value={`${formatDurationDecimal(summary.unbilledHours * 60)} Hours`}
            className="bg-card"
            iconClassName="text-primary"
            data-testid="metric-unbilled-hours"
          />
          <MetricCard
            icon={DollarSign}
            label="Unbilled Amount"
            value={formatCurrency(summary.unbilledAmount, defaultCurrency)}
            className="bg-card"
            iconClassName="text-primary"
            data-testid="metric-unbilled-amount"
          />
        </div>
      </FadeIn>

      <FadeIn delay={0.15}>
        {dashboardEntries.length === 0 ? (
          <EmptyState
            title="No tasks yet"
            description="Add your first task to start tracking your work."
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <DashboardTable
                entries={dashboardEntries}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDeleteRequest}
              />
            </CardContent>
          </Card>
        )}
      </FadeIn>

      <TaskDialog
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingTask(null);
        }}
        task={editingTask}
        projects={projectOptions}
        members={(() => {
          const map = new Map<string, { id: string; name: string }>();
          for (const entry of dashboardEntries) {
            for (const m of entry.members) {
              if (!map.has(m.id)) map.set(m.id, m);
            }
          }
          return Array.from(map.values());
        })()}
        initialMode="edit"
        onSave={handleSaveEdit}
      />

      <DeleteConfirmation
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete task?"
        description={<>You are about to delete <strong>{pendingDeleteTask?.title ?? 'this task'}</strong>. This action cannot be undone.</>}
        onConfirm={handleDeleteConfirm}
        testId="dashboard-delete-confirmation"
        actionsTestId="dashboard-delete-confirmation-actions"
        confirmTestId="dashboard-delete-confirm"
      />
    </>
  );
}

interface MetricCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  className?: string;
  iconClassName?: string;
  'data-testid'?: string;
}

function MetricCard({ icon: Icon, label, value, className, iconClassName, 'data-testid': testId }: MetricCardProps) {
  return (
    <Card className={className} data-testid={testId}>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border bg-surface">
          <Icon className={`h-5 w-5 ${iconClassName}`} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
